import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { Dugme, HataKutusu } from './temel';

/**
 * KİPLİ PENCERE (modal).
 *
 * GEREKÇE ZORUNLULUĞU ALTI YERDE VAR — puantaj düzeltme, izin/görev reddi,
 * iptal, dönem kilidi, kalibrasyon ve parametre değişikliği. Altı ekranda
 * ayrı yazılsaydı biri gerekçeyi `trim()`siz gönderir ve sunucu
 * `@IsNotEmpty` ile 400 döndürürdü; kullanıcı boşluk yazdığını anlamazdı.
 *
 * `<dialog>` etiketi KULLANILMIYOR: `showModal()` zorunlu ve React'in
 * bildirimsel akışıyla eşleşmiyor; ayrıca jsdom `showModal`ı desteklemiyor
 * ve bileşen sınanamaz hâle gelirdi.
 */
export function Diyalog({
  baslik,
  aciklama,
  cocuk,
  kapat,
  genis = false,
}: {
  baslik: string;
  aciklama?: string;
  cocuk: ReactNode;
  kapat: () => void;
  genis?: boolean;
}) {
  const kutu = useRef<HTMLDivElement>(null);
  const basrikId = useId();

  useEffect(() => {
    const tusa = (olay: KeyboardEvent) => {
      if (olay.key === 'Escape') kapat();
    };

    document.addEventListener('keydown', tusa);

    /*
      ODAK KUTUYA ALINIR. Alınmazsa Escape dinleyicisi belgede olduğu için
      çalışır ama Tab tuşu kullanıcıyı arka plandaki tablo satırlarında
      gezdirir; ekran okuyucu da diyaloğu hiç duyurmaz.
    */
    kutu.current?.focus();

    return () => document.removeEventListener('keydown', tusa);
  }, [kapat]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      /*
        ARKA PLANA TIKLAMA KAPATIR ama YALNIZ arka plana: `currentTarget`
        denetimi olmadan kutunun içindeki her tıklama da kapatırdı.
      */
      onMouseDown={(olay) => {
        if (olay.target === olay.currentTarget) kapat();
      }}
    >
      <div
        ref={kutu}
        role="dialog"
        aria-modal="true"
        aria-labelledby={basrikId}
        tabIndex={-1}
        className={`max-h-[90vh] w-full overflow-y-auto rounded-lg border border-slate-200 bg-white p-6 shadow-lg outline-none ${
          genis ? 'max-w-2xl' : 'max-w-md'
        }`}
      >
        <h2 id={basrikId} className="text-base font-semibold text-slate-900">
          {baslik}
        </h2>
        {aciklama && <p className="mt-1 text-sm text-slate-600">{aciklama}</p>}
        <div className="mt-5">{cocuk}</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

export interface GerekceSecenegi {
  kod: string;
  etiket: string;
  aciklama?: string;
}

/**
 * GEREKÇELİ ONAY DİYALOĞU.
 *
 * `enCokKarakter` VARSAYILAN 500: backend DTO'larının çoğunda
 * `@MaxLength(500)`. Dönem kilidi ve izin talebi 1000 istiyor, o ekranlar
 * değeri geçer. Sunucu sınırının altında tutmak sayaçla uyarmayı sağlar;
 * üstünde tutulsaydı kullanıcı yazmayı bitirdikten sonra 400 alırdı.
 *
 * ASGARİ UZUNLUK DAYATILMAZ. Sunucu yalnız `@IsNotEmpty` istiyor; panelde
 * ayrı bir alt sınır konsaydı sunucunun kabul edeceği bir gerekçe burada
 * reddedilir ve iki kural birbirinden sürüklenirdi. Yalnız boş ve
 * yalnız-boşluk girdi engellenir.
 *
 * `zorunluMu = false` İSTİSNADIR ve gerçek bir uç şeklinden gelir: izin
 * onayında gövde `OnayDto` ve `aciklama` İSTEĞE BAĞLI. Varsayılan `true`
 * kalıyor — red, iptal, kilit ve düzeltme uçlarının hepsi `@IsNotEmpty`
 * istiyor ve varsayılanın gevşemesi o çağrıları sessizce 400'e açardı.
 */
export function GerekceDiyalogu({
  baslik,
  aciklama,
  etiket = 'Gerekçe',
  ipucu,
  zorunluMu = true,
  onayEtiketi = 'Onayla',
  onayTuru = 'birincil',
  enCokKarakter = 500,
  secenekler,
  secenekEtiketi = 'Sebep',
  bekliyor = false,
  hata,
  kapat,
  onayla,
  ustBilgi,
}: {
  baslik: string;
  aciklama?: string;
  etiket?: string;
  ipucu?: string;
  /** `false` ise metin boş bırakılabilir (izin onayındaki `aciklama` gibi). */
  zorunluMu?: boolean;
  onayEtiketi?: string;
  onayTuru?: 'birincil' | 'tehlike';
  enCokKarakter?: number;
  /** Verilirse önce kapalı bir sebep kodu seçilir (düzeltme gerekçeleri gibi). */
  secenekler?: GerekceSecenegi[];
  secenekEtiketi?: string;
  bekliyor?: boolean;
  hata?: unknown;
  kapat: () => void;
  onayla: (gerekce: string, secenekKodu?: string) => void;
  /**
   * Sebep ve gerekçe alanlarının ÜSTÜNE eklenen ek alan(lar).
   *
   * Puantaj düzeltmesinde yeni saat de gerekiyor; ayrı bir diyalog yazmak
   * yerine bu bileşene alan EKLENİYOR. Yeni prop'lar (saat, tarih, tutar)
   * tek tek eklenseydi bileşen her yeni kullanımda büyür ve genel olmaktan
   * çıkardı — burada içerik ÇAĞIRANA bırakılıyor.
   */
  ustBilgi?: ReactNode;
}) {
  const [gerekce, gerekceYaz] = useState('');
  const [secenekKodu, secenekKoduYaz] = useState('');

  const kirpilmis = gerekce.trim();
  const secenekGerekli = (secenekler?.length ?? 0) > 0;
  const gonderilebilir =
    (!zorunluMu || kirpilmis.length > 0) &&
    kirpilmis.length <= enCokKarakter &&
    (!secenekGerekli || secenekKodu !== '');

  const secilen = secenekler?.find((s) => s.kod === secenekKodu);

  return (
    <Diyalog
      baslik={baslik}
      aciklama={aciklama}
      kapat={kapat}
      cocuk={
        <div className="space-y-4">
          {ustBilgi}

          {secenekGerekli && (
            <div>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700">
                  {secenekEtiketi}
                </span>
                <select
                  value={secenekKodu}
                  onChange={(olay) => secenekKoduYaz(olay.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
                >
                  {/* Yer tutucu olmasaydı kutu ilk sebebi seçili gösterir ve
                    kullanıcı hiç seçim yapmadan "seçtim" sanırdı. */}
                  <option value="">Seçin…</option>
                  {secenekler?.map((s) => (
                    <option key={s.kod} value={s.kod}>
                      {s.etiket}
                    </option>
                  ))}
                </select>
              </label>
              {/* Açıklama ETİKETİN DIŞINDA: içeride olsaydı seçim kutusunun
                  erişilebilir adına eklenir ve her seçimde ad değişirdi. */}
              {secilen?.aciklama !== undefined && secilen.aciklama !== '' && (
                <p className="mt-1 text-xs text-slate-500">{secilen.aciklama}</p>
              )}
            </div>
          )}

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">{etiket}</span>
            <textarea
              value={gerekce}
              rows={4}
              onChange={(olay) => gerekceYaz(olay.target.value)}
              className="w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
            />
            <span className="mt-1 flex items-center justify-between text-xs">
              <span className="text-slate-500">
                {ipucu ?? (zorunluMu ? 'Gerekçe kalıcı kayıttır.' : 'İsteğe bağlı.')}
              </span>
              <span
                className={kirpilmis.length > enCokKarakter ? 'text-red-600' : 'text-slate-400'}
              >
                {kirpilmis.length}/{enCokKarakter}
              </span>
            </span>
          </label>

          <HataKutusu hata={hata} />

          <div className="flex justify-end gap-2">
            <Dugme tur="ikincil" tikla={kapat} pasif={bekliyor} cocuk="Vazgeç" />
            <Dugme
              tur={onayTuru}
              bekliyor={bekliyor}
              pasif={!gonderilebilir}
              tikla={() => onayla(kirpilmis, secenekGerekli ? secenekKodu : undefined)}
              cocuk={onayEtiketi}
            />
          </div>
        </div>
      }
    />
  );
}
